export const API = '/api/v1';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly issues?: { path: string; message: string }[],
  ) {
    super(message);
  }
}

/** Mensaje legible para mostrar en pantalla. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.issues?.length) return err.issues.map((i) => (i.path ? `${i.path}: ${i.message}` : i.message)).join(' · ');
    return err.message;
  }
  return 'No se pudo conectar con el servidor';
}

// La cabecera X-Requested-With es obligatoria en peticiones con cookie que modifican (CSRF).
const baseHeaders = { 'x-requested-with': 'ocr1sk-web' };

let onUnauthorized: (() => void) | null = null;
export const setUnauthorizedHandler = (fn: () => void) => {
  onUnauthorized = fn;
};

export async function api<T = unknown>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    method: init.method ?? 'GET',
    credentials: 'same-origin',
    headers: init.body !== undefined ? { ...baseHeaders, 'content-type': 'application/json' } : baseHeaders,
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    if (res.status === 401 && !path.startsWith('/auth/login')) onUnauthorized?.();
    throw new ApiError(res.status, data?.error ?? 'HTTP_ERROR', data?.message ?? `Error ${res.status}`, data?.issues);
  }
  return data as T;
}

export const get = <T>(path: string) => api<T>(path);
export const post = <T>(path: string, body: unknown = {}) => api<T>(path, { method: 'POST', body });
export const patch = <T>(path: string, body: unknown) => api<T>(path, { method: 'PATCH', body });
export const put = <T>(path: string, body: unknown) => api<T>(path, { method: 'PUT', body });
export const del = (path: string) => api<void>(path, { method: 'DELETE' });

export function qs(params: Record<string, string | number | undefined | null>) {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') s.set(k, String(v));
  const str = s.toString();
  return str ? `?${str}` : '';
}

/** Subida con progreso (fetch no lo expone). */
export function uploadFile(
  file: File,
  mode: 'skip-text' | 'force',
  onProgress: (fraction: number) => void,
  groupId?: number,
): Promise<import('./types').Job> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/jobs${qs({ mode, groupId })}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader('x-requested-with', baseHeaders['x-requested-with']);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () => {
      let data: any = null;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        /* vacío */
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data);
      else reject(new ApiError(xhr.status, data?.error ?? 'HTTP_ERROR', data?.message ?? `Error ${xhr.status}`));
    };
    xhr.onerror = () => reject(new ApiError(0, 'NETWORK', 'Error de red'));
    const form = new FormData();
    form.append('file', file, file.name);
    xhr.send(form);
  });
}
